import { calculateDiscountedPrice, getDiscountConfiguration } from '@vy/services';
import {
  CreditPurchaseConfigSchema,
  PlanInputFormFixedProductSchema,
  PlanInputFormUsageProductSchema,
  TrialDurationSchema,
} from '@vy/types';
import { isNotNullOrUndefined, oz } from '@vy/utils';
import { addProductConfigurationIssues } from './plan-input-form-product-rules';
import { PlanSettingsInputFormSchema } from './plan-settings-input-form';
import { isPercentageDiscountOverLimit } from './plan-input-form-validations';

export const PlanInputFormSchema = PlanSettingsInputFormSchema.omit({
  trial: true,
})
  .extend({
    fixedProducts: oz.array(PlanInputFormFixedProductSchema),
    usageProducts: oz.array(PlanInputFormUsageProductSchema),
    promotionProducts: oz.array(PlanInputFormFixedProductSchema),
    credits: oz.array(CreditPurchaseConfigSchema),
    trial: TrialDurationSchema.nullable().optional(),
  })
  .partial()
  .refine(
    (data) =>
      (data?.fixedProducts?.length ?? 0) > 0 ||
      (data?.usageProducts?.length ?? 0) > 0 ||
      (data?.credits?.length ?? 0) > 0,
    { message: 'Plan must have at least one product or credit', path: ['productsValidation'] },
  )
  .superRefine((data, ctx) => {
    addProductConfigurationIssues(data, ctx);

    data.fixedProducts?.forEach((product, index) => {
      const discountConfiguration = getDiscountConfiguration(product);
      const priceConfiguration = product.variants[0].pricing.priceConfiguration;
      const hasPrice = isNotNullOrUndefined(priceConfiguration) && 'price' in priceConfiguration;

      if (isNotNullOrUndefined(discountConfiguration) && hasPrice) {
        const { type, amountToDeduct } = discountConfiguration;

        const priceAfterDiscount = calculateDiscountedPrice(
          priceConfiguration.price ?? 0,
          type,
          amountToDeduct,
          1, // we don't need to multiply the discount here because this is apparently price per seat
        );

        if (priceAfterDiscount < 0) {
          ctx.addIssue({
            path: [
              'fixedProducts',
              index,
              'variants',
              0,
              'pricing',
              'priceConfiguration',
              'discountConfiguration',
              'amountToDeduct',
            ],
            message: 'Discount amount cannot exceed the total price',
            code: 'invalid_literal',
            expected: 'a value less than or equal to the price',
            received: amountToDeduct,
          });
        }
      }
    });

    data.usageProducts?.forEach((product, index) => {
      const priceConfiguration = product.variants[0]?.pricing?.priceConfiguration;
      const discountConfiguration =
        isNotNullOrUndefined(priceConfiguration) && 'discountConfiguration' in priceConfiguration
          ? priceConfiguration.discountConfiguration
          : undefined;

      if (isPercentageDiscountOverLimit(discountConfiguration)) {
        ctx.addIssue({
          code: 'custom',
          message: 'Percentage discount cannot exceed 100%',
          path: [
            'usageProducts',
            index,
            'variants',
            0,
            'pricing',
            'priceConfiguration',
            'discountConfiguration',
            'amountToDeduct',
          ],
        });
      }
    });
  });

export type PlanInputForm = oz.infer<typeof PlanInputFormSchema>;
